import { Auth0Provider } from '@auth0/auth0-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Theme } from '@radix-ui/themes';
import { Main } from 'components/main';
import env from 'config';
import { LogRocketProvider } from 'contexts/log-rocket.context';
import useRegionRedirect from 'hooks/useRegionRedirect';
import { useSocket } from 'hooks/useSocket';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { RADIX, RadixAppearance } from 'lib_ts/enums/radix-ui';
import { useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useAuthStore } from 'stores/use-auth-store';
import useBpSummaryStore from 'stores/use-bp-summary-store';
import useMiscStore from 'stores/use-misc-store';
import './App.scss';
import './translations/i18n';

// if we deprecate the navigation-blocker / useBlocker for Pitch Uploader, we can make this a declarative router like the rest of the app
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <>
        <Main />
        {env.identifier !== 'local' && <LogRocketProvider />}
      </>
    ),
  },
]);

const App = () => {
  if (!env.debug_messages) {
    console.debug = () => {};
  }

  if (!env.production) {
    console.warn(
      'Render events will be logged twice when running in dev mode (see React.StrictMode)'
    );
  }

  const current = useAuthStore((s) => s.current);

  // check whether we need to offer to redirect the user to their local site
  useRegionRedirect();

  useSocket();

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  useEffect(() => {
    document.body.setAttribute(
      'class',
      `radix-themes ${isDarkMode ? RadixAppearance.Dark : RadixAppearance.Light}`
    );

    // toggle light vs dark appearance when F8 is pressed
    const callback = (event: KeyboardEvent) => {
      switch (event.code) {
        case 'F8': {
          event.preventDefault();
          event.stopPropagation();

          const isDarkMode = useMiscStore.getState().isDarkMode;

          useMiscStore
            .getState()
            .setAppearance(
              isDarkMode ? RadixAppearance.Light : RadixAppearance.Dark
            );
          break;
        }

        case 'F9': {
          if (current.role !== UserRole.admin) {
            // only allow SA to access this feature
            break;
          }

          event.preventDefault();
          event.stopPropagation();

          useBpSummaryStore.getState().toggleOpen();
          break;
        }

        default: {
          break;
        }
      }
    };

    document.addEventListener('keydown', callback);

    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [current.role, isDarkMode]);

  useEffect(() => {
    if (env.identifier.startsWith('prod')) {
      // do nothing
      return;
    }

    document.title = `Trajekt - ${StringHelper.capitalize(env.identifier)}`;
  }, [env.identifier]);

  return (
    <Theme
      id="ThemeWrapper"
      accentColor={RADIX.COLOR.ACCENT}
      appearance={isDarkMode ? RadixAppearance.Dark : RadixAppearance.Light}
    >
      <Tooltip.Provider delayDuration={50}>
        <Auth0Provider
          domain={env.integrations.auth0.domain}
          clientId={env.integrations.auth0.clientId}
          authorizationParams={{
            redirect_uri: window.location.origin,
            audience: env.integrations.auth0.audience,
          }}
          cacheLocation="localstorage"
          useRefreshTokens
        >
          <RouterProvider router={router} />
        </Auth0Provider>
      </Tooltip.Provider>
    </Theme>
  );
};

export default withTranslation()(App);
